import { Device } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useGroupByName } from "../groups/useGroupByName";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { FUEL_CELL_GROUP_NAME } from "@/constants/groupTemplates";

const RESULTS_LIMIT = 50000;

/**
 * Which vehicles run on hydrogen.
 *
 * A device carries no field saying how it is powered, so the fuel-cell vehicles are the ones in
 * the {@link FUEL_CELL_GROUP_NAME} group. The membership test is left to Geotab through
 * `groupFilterCondition` rather than compared against the group id here, because a vehicle may
 * sit in a group *below* that one and a comparison by id would miss it.
 *
 * Asked for once for the whole fleet rather than per screenful: which vehicles are fuel-cell
 * changes when a vehicle is bought, not while somebody watches the map.
 *
 * @returns The ids of the hydrogen vehicles; empty while the group is still being looked up.
 */
export function useFuelCellDeviceIds(): ReadonlySet<string> {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { data: group } = useGroupByName(FUEL_CELL_GROUP_NAME);
  const groupId = group?.id;

  const { data: deviceIds } = useQuery<string[]>({
    queryKey: ["fuelCellDeviceIds", activeSessionId, groupId],
    enabled: !!groupId,

    queryFn: async ({ signal }) => {
      const devices: Device[] = await geotabClient.get(
        "Device",
        {
          // Membership as it stands now; without this, vehicles that used to be in the group come
          // back too.
          fromDate: new Date(),
          groupFilterCondition: { groupId },
          propertySelector: { fields: ["id"], isIncluded: true },
        },
        RESULTS_LIMIT,
        signal,
      );

      return (devices ?? []).map(device => device.id).filter((id): id is string => !!id);
    },
  });

  // Memoised because callers key their own work on this set; a fresh Set on every render would
  // have the map re-derive every vehicle's row sixty times a second.
  return useMemo(() => new Set(deviceIds), [deviceIds]);
}
